/** @jsxImportSource theme-ui */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, List } from 'semantic-ui-react';

import { InsightsState } from 'app-redux/InsightsState';
import { RiskModel } from 'client/dto';
import { RiskLikelihood, RiskMagnitude } from 'models/RiskCategories';
import { getRiskLikelihood, getRiskMagnitude } from 'utils/modelHelpers';
import { formatNumberOfCases } from 'utils/stringFormattingHelpers';

import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';

import diseaseActivitiesGlobalSelectors from 'diseaseActivitiesGlobal/diseaseActivitiesGlobal.selectors';
import { SparkGraphGlobal } from 'diseaseActivitiesGlobal/SparkGraphGlobal';
import { openPanel } from 'diseaseActivityPanels/diseaseActivityPanels.saga';
import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';

import CaptionWithSubtitle from './CaptionWithSubtitle';
import { diseaseCardStyle } from './diseases.styles';
import RiskLikelihoodPopup from './RiskLikelihoodPopup';

type DiseaseGlobalCardProps = {
  diseaseId: number;
};
const DiseaseGlobalCard: React.FC<DiseaseGlobalCardProps> = ({ diseaseId }) => {
  const dispatch = useDispatch();
  const diseaseName: string = useSelector<InsightsState, string>(
    diseaseActivitiesGlobalSelectors.getDiseaseName(diseaseId)
  );
  const riskModel: RiskModel = useSelector<InsightsState, RiskModel>(
    diseaseActivitiesGlobalSelectors.getRiskModel(diseaseId)
  );
  const reportedCases: number = useSelector<InsightsState, number>(
    diseaseActivitiesGlobalSelectors.getReportedCases(diseaseId)
  );
  const isSelected = useSelector(diseaseActivityPanelsSelectors.isDiseaseSelected(diseaseId));

  const riskMagnitude: RiskMagnitude = getRiskMagnitude(riskModel);
  const riskLikelihood: RiskLikelihood = getRiskLikelihood(riskModel);
  return (
    <List.Item
      data-diseaseid={diseaseId}
      sx={diseaseCardStyle}
      active={isSelected}
      onClick={e => dispatch(openPanel({ panel: 'DiseaseActivityList', diseaseId: diseaseId }))}
    >
      <List.Content>
        <List.Header>
          <FlexGroup
            suffix={
              <>
                <RiskLikelihoodPopup
                  riskLikelihood={riskLikelihood}
                  riskDirectionType="exportation"
                />
              </>
            }
          >
            <Typography variant="subtitle2" color="stone90">
              {diseaseName}
            </Typography>
          </FlexGroup>
        </List.Header>
        <List.Description>
          <Grid
            columns={3}
            relaxed="very"
            sx={{
              '.icon.bd-icon': {
                fontSize: '14px',
              },
            }}
          >
            <Grid.Row>
              <Grid.Column>
                <SparkGraphGlobal diseaseId={diseaseId} />
              </Grid.Column>
              <Grid.Column>
                <CaptionWithSubtitle
                  caption="Global reported cases"
                  subtitle={formatNumberOfCases(reportedCases)}
                />
              </Grid.Column>
              <Grid.Column>
                <CaptionWithSubtitle
                  caption="Estimated case exportations per month"
                  subtitle={riskMagnitude}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </List.Description>
      </List.Content>
    </List.Item>
  );
};
export default DiseaseGlobalCard;
